import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { BookHalf } from "react-bootstrap-icons";
import styled from "styled-components";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import { getDefaultDateRange, getWidgetTheme } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, MetricConfig, DocsSidebarEntry, WidgetFieldSchema } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { PopPeriodBanner } from "../../../components/analytics";
import InteractiveLineChart from "../../../components/analytics/InteractiveLineChart";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import MetricsTable, { ColumnGroup, SortConfig } from "../../../components/analytics/MetricsTable";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";

type RecsDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type RecsDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    recsRows: RecsRow[];
    recsDashRows: RecsApiRow[];
    recsDailyRows: RecsApiRow[];
    recsHourlyRows: RecsApiRow[];
    recsBrandBreakdownRows: RecsApiRow[];
    brandBreakdownSort: SortConfig;
    topBrandsVisibleSeries: Record<string, boolean>;
    hoveredTopBrandsSeries: string | null;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};

type RecsApiRow = Record<string, string | number | null | undefined>;

type RecsRow = {
    parameterRuleId: string;
    slotName: string;
    modelName: string;
    users: number;
    usersPercentChange: number | null;
    plays: number;
    playsPercentChange: number | null;
    views: number;
    viewsPercentChange: number | null;
    playsPerUser: number | null;
    playsPerUserPercentChange: number | null;
    sessionConversionPct: number | null;
    sessionConversionPctChange: number | null;
    playedBrands: number;
    playedBrandsPercentChange: number | null;
    watchtime: number;
    watchtimePercentChange: number | null;
};

type RecsDashParsedRow = {
    brandRank: number;
    brandName: string;
    brandPlays: number;
    slotName: string;
    parameterRuleId: string;
    rulePlays: number;
    ruleSharePct: number | null;
};

type RecsDashChartPoint = {
    brandName: string;
    brandRank: number;
    brandPlays: number;
    [seriesKey: string]: string | number;
};

const STACK_COLORS: string[] = [
    "#4E79A7", // blue
    "#F28E2B", // orange
    "#59A14F", // green
    "#E15759", // red
    "#B07AA1", // purple
    "#76B7B2", // teal
    "#EDC948", // yellow
    "#FF9DA7", // pink
    "#9C755F", // brown
    "#BAB0AC", // gray
    "#2F4B7C", // deep blue
    "#A05195"  // magenta
];

const LINE_COLORS: string[] = [
    "#4E79A7",
    "#F28E2B",
    "#59A14F",
    "#E15759",
    "#B07AA1",
    "#76B7B2",
    "#EDC948",
    "#FF9DA7"
];

const RESP: any = ResponsiveContainer as any;
const CCHART: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

const RuleSection = styled.div`
    margin-top: 24px;
`;

class RecsDashboard extends React.Component<RecsDashboardProps, RecsDashboardState> {
    constructor(props: RecsDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            devicePlatform: []
        };

        this.state = {
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                devicePlatforms: []
            },
            filterKey: 0,
            recsRows: [],
            recsDashRows: [],
            recsDailyRows: [],
            recsHourlyRows: [],
            recsBrandBreakdownRows: [],
            brandBreakdownSort: { key: "rec_plays", direction: "desc" },
            topBrandsVisibleSeries: {},
            hoveredTopBrandsSeries: null,
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { recsRows, recsDailyRows, recsBrandBreakdownRows, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        if (recsRows.length > 0) {
            data.push({
                title: "Recommendations Summary",
                columns: [
                    { key: "slotName", label: "Slot" },
                    { key: "modelName", label: "Model" },
                    { key: "users", label: "Users", format: "number" },
                    { key: "plays", label: "Plays", format: "number" },
                    { key: "views", label: "Views", format: "number" },
                    { key: "playsPerUser", label: "Plays/User", format: "number" },
                    { key: "sessionConversionPct", label: "Session Conversion %", format: "percent" },
                    { key: "playedBrands", label: "Played Brands", format: "number" },
                    { key: "watchtime", label: "Watchtime", format: "number" }
                ],
                rows: recsRows,
                metadata: { filters: appliedFilters, pageKey: "recs_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (recsDailyRows.length > 0) {
            data.push({
                title: "Recommendations Daily",
                columns: Object.keys(recsDailyRows[0]).map(k => ({ key: k, label: k })),
                rows: recsDailyRows,
                metadata: { filters: appliedFilters, pageKey: "recs_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (recsBrandBreakdownRows.length > 0) {
            data.push({
                title: "Brand Breakdown",
                columns: Object.keys(recsBrandBreakdownRows[0]).map(k => ({ key: k, label: k })),
                rows: recsBrandBreakdownRows,
                metadata: { filters: appliedFilters, pageKey: "recs_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Recommendations Dashboard",
            pageKey: "recs_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private toNumber(value: number | string | undefined | null): number | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatChange(changePercent: number | null): string {
        if (changePercent === null || changePercent === undefined) {
            return "No change data";
        }

        const sign = changePercent > 0 ? "+" : "";
        return `${sign}${changePercent.toFixed(2)}%`;
    }

    private getSchemaDescription(
        schema: Record<string, WidgetFieldSchema> | undefined,
        keys: string[]
    ): string | undefined {
        for (const key of keys) {
            const description = schema?.[key]?.description;
            if (description) {
                return description;
            }
        }

        return undefined;
    }

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [recsResponse, recsDashResponse, recsDailyResponse, recsHourlyResponse, recsBrandBreakdownResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_plays",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_attributions",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_daily_counts",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_hourly_counts",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_brand_breakdown",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.devicePlatform
                )
            ]);

            const rowsRaw = (recsResponse as any)?.result;

            const recsRows: RecsRow[] = (Array.isArray(rowsRaw) ? rowsRaw : [])
                .map((row: RecsApiRow) => {
                    const plays = this.toNumber(row["plays"]) ?? 0;
                    const parameterRuleId = String(row["parameters_rule_id"] || row["parameter_rule_id"] || "Unknown");

                    return {
                        parameterRuleId,
                        slotName: String(row["slot_name"] || ""),
                        modelName: String(row["model_name"] || ""),
                        users: this.toNumber(row["users"]) ?? 0,
                        usersPercentChange: this.toNumber(row["users_percent_change"]),
                        plays,
                        playsPercentChange: this.toNumber(row["plays_percent_change"]),
                        views: this.toNumber(row["views"] ?? row["view_count"]) ?? 0,
                        viewsPercentChange: this.toNumber(row["views_percent_change"] ?? row["view_count_percent_change"]),
                        playsPerUser: this.toNumber(row["plays_per_user"]),
                        playsPerUserPercentChange: this.toNumber(row["plays_per_user_percent_change"]),
                        sessionConversionPct: this.toNumber(row["session_conversion_pct"]),
                        sessionConversionPctChange: this.toNumber(row["session_conversion_pct_change"]),
                        playedBrands: this.toNumber(row["played_brands"]) ?? 0,
                        playedBrandsPercentChange: this.toNumber(row["played_brands_percent_change"]),
                        watchtime: this.toNumber(row["watchtime"]) ?? 0,
                        watchtimePercentChange: this.toNumber(row["watchtime_percent_change"])
                    };
                })
                .filter((row: RecsRow) => row.plays > 0)
                .sort((a: RecsRow, b: RecsRow) => b.plays - a.plays);

            const recsDashRaw = (recsDashResponse as any)?.result;
            const recsDashRows = Array.isArray(recsDashRaw) ? recsDashRaw as RecsApiRow[] : [];

            const recsDailyRaw = (recsDailyResponse as any)?.result;
            const recsDailyRows = Array.isArray(recsDailyRaw) ? recsDailyRaw as RecsApiRow[] : [];

            const recsHourlyRaw = (recsHourlyResponse as any)?.result;
            const recsHourlyRows = Array.isArray(recsHourlyRaw) ? recsHourlyRaw as RecsApiRow[] : [];

            const recsBrandBreakdownRaw = (recsBrandBreakdownResponse as any)?.result;
            const recsBrandBreakdownRows = Array.isArray(recsBrandBreakdownRaw) ? recsBrandBreakdownRaw as RecsApiRow[] : [];

            this.setState({ recsRows, recsDashRows, recsDailyRows, recsHourlyRows, recsBrandBreakdownRows });

            /* eslint-disable camelcase */
            const widgetResponses: Array<{ key: string; response: unknown }> = [
                { key: "recs_plays", response: recsResponse },
                { key: "recs_attributions", response: recsDashResponse },
                { key: "recs_daily_counts", response: recsDailyResponse },
                { key: "recs_hourly_counts", response: recsHourlyResponse },
                { key: "recs_brand_breakdown", response: recsBrandBreakdownResponse }
            ];
            const sidebarEntries: DocsSidebarEntry[] = widgetResponses
                .map(({ key, response: r }) => {
                    const meta = (r as any)?.widget;
                    if (!meta) return null;
                    const entry: DocsSidebarEntry = {
                        widgetKey: key,
                        displayName: meta.display_name,
                        description: meta.description,
                        schema: (r as any)?.schema
                    };
                    return entry;
                })
                .filter((e): e is DocsSidebarEntry => e !== null);
            /* eslint-enable camelcase */
            this.setState({ sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching Recs dashboard data", err);
            this.setState({ error: "Failed to fetch Recs dashboard data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private getRecsMetricCards(
        row: RecsRow,
        schema?: Record<string, WidgetFieldSchema>
    ): MetricCardItem[] {
        return [
            {
                key: `${row.parameterRuleId}-plays`,
                label: "Plays",
                value: row.plays,
                changePercent: row.playsPercentChange,
                valueFormat: "number",
                description: this.getSchemaDescription(schema, ["plays"])
            },
            {
                key: `${row.parameterRuleId}-users`,
                label: "Users",
                value: row.users,
                changePercent: row.usersPercentChange,
                valueFormat: "number",
                description: this.getSchemaDescription(schema, ["users"])
            },
            {
                key: `${row.parameterRuleId}-views`,
                label: "Views",
                value: row.views,
                changePercent: row.viewsPercentChange,
                valueFormat: "number",
                description: this.getSchemaDescription(schema, ["views", "view_count"])
            },
            {
                key: `${row.parameterRuleId}-brands-played`,
                label: "Brands Played",
                value: row.playedBrands,
                changePercent: row.playedBrandsPercentChange,
                valueFormat: "number",
                description: this.getSchemaDescription(schema, ["played_brands"])
            },
            {
                key: `${row.parameterRuleId}-plays-per-user`,
                label: "Plays / user",
                value: row.playsPerUser,
                changePercent: row.playsPerUserPercentChange,
                valueFormat: "decimal",
                description: this.getSchemaDescription(schema, ["plays_per_user"])
            },
            {
                key: `${row.parameterRuleId}-session-conversion`,
                label: "Session Conversion",
                value: row.sessionConversionPct,
                changePercent: row.sessionConversionPctChange,
                valueFormat: "percent",
                description: this.getSchemaDescription(schema, ["session_conversion_pct"])
            },
            {
                key: `${row.parameterRuleId}-watchtime`,
                label: "Watchtime",
                value: row.watchtime,
                changePercent: row.watchtimePercentChange,
                valueFormat: "durationSeconds",
                description: this.getSchemaDescription(schema, ["watchtime"])
            }
        ];
    }

    private renderRecsMetrics(
        row: RecsRow,
        schema?: Record<string, WidgetFieldSchema>
    ): ReactNode {
        return (
            <MetricCards
                metrics={this.getRecsMetricCards(row, schema)}
                theme={getWidgetTheme(true)}
                noChangeLabel="No change data"
            />
        );
    }

    private parseRecsDashRows(): RecsDashParsedRow[] {
        const { recsDashRows } = this.state;

        return recsDashRows
            .map((row: RecsApiRow) => {
                const brandRank = this.toNumber(row["brand_rank"]) ?? 0;
                const brandName = String(row["brand_name"] || "Unknown");
                const brandPlays = this.toNumber(row["brand_plays"]) ?? 0;
                const slotName = String(row["slot_name"] || "").trim();
                const parameterRuleId = String(row["parameters_rule_id"] || "Unknown");
                const rulePlays = this.toNumber(row["rule_plays"]) ?? 0;
                const ruleSharePct = this.toNumber(row["rule_share_pct"]);

                return {
                    brandRank,
                    brandName,
                    brandPlays,
                    slotName,
                    parameterRuleId,
                    rulePlays,
                    ruleSharePct
                };
            })
            .filter((row: RecsDashParsedRow) => row.rulePlays > 0);
    }

    private getStackColor(index: number): string {
        return STACK_COLORS[index % STACK_COLORS.length];
    }

    private getRuleToSlotNameMap(): Record<string, string> {
        const ruleToSlot: Record<string, string> = {};
        this.state.recsRows.forEach((row: RecsRow) => {
            const slotName = (row.slotName || "").trim();
            if (slotName) {
                ruleToSlot[row.parameterRuleId] = slotName;
            }
        });
        return ruleToSlot;
    }

    private getRecsDashSeriesKey(row: RecsDashParsedRow, ruleToSlotName: Record<string, string>): string {
        return row.slotName || ruleToSlotName[row.parameterRuleId] || row.parameterRuleId;
    }

    private getRecsDashChartData(): { chartData: RecsDashChartPoint[]; seriesKeys: string[] } {
        const parsedRows = this.parseRecsDashRows();
        const ruleToSlotName = this.getRuleToSlotNameMap();
        const pointsByBrand = new Map<string, RecsDashChartPoint>();
        const seriesTotals: Record<string, number> = {};

        parsedRows.forEach((row: RecsDashParsedRow) => {
            const pointKey = `${row.brandRank}-${row.brandName}`;
            const existingPoint = pointsByBrand.get(pointKey);

            if (!existingPoint) {
                pointsByBrand.set(pointKey, {
                    brandName: row.brandName,
                    brandRank: row.brandRank,
                    brandPlays: row.brandPlays
                });
            } else if (!existingPoint.brandPlays && row.brandPlays > 0) {
                existingPoint.brandPlays = row.brandPlays;
            }

            // "All attributed" is an aggregate, not an individual stack segment.
            if (row.parameterRuleId === "All attributed") {
                return;
            }

            const seriesKey = this.getRecsDashSeriesKey(row, ruleToSlotName);
            const shareValue = row.ruleSharePct ?? 0;

            const point = pointsByBrand.get(pointKey);
            if (!point) return;

            const currentValue = typeof point[seriesKey] === "number" ? (point[seriesKey] as number) : 0;
            point[seriesKey] = currentValue + shareValue;
            seriesTotals[seriesKey] = (seriesTotals[seriesKey] || 0) + shareValue;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(pointsByBrand.values())
            .sort((a, b) => a.brandRank - b.brandRank);

        // Ensure all series keys exist on each point for stable stacked rendering.
        const normalizedChartData = chartData.map((point) => {
            const normalizedPoint: RecsDashChartPoint = { ...point };
            seriesKeys.forEach((seriesKey) => {
                if (typeof normalizedPoint[seriesKey] !== "number") {
                    normalizedPoint[seriesKey] = 0;
                }
            });
            return normalizedPoint;
        });

        return { chartData: normalizedChartData, seriesKeys };
    }

    private isTopBrandsSeriesVisible(seriesKey: string): boolean {
        const value = this.state.topBrandsVisibleSeries[seriesKey];
        return value === undefined ? true : value;
    }

    private handleTopBrandsSeriesToggle = (seriesKey: string): void => {
        this.setState((prevState) => ({
            topBrandsVisibleSeries: {
                ...prevState.topBrandsVisibleSeries,
                [seriesKey]: !this.isTopBrandsSeriesVisible(seriesKey)
            }
        }));
    };

    private handleTopBrandsToggleAll = (seriesKeys: string[]): void => {
        const allVisible = seriesKeys.every((key: string) => this.isTopBrandsSeriesVisible(key));
        const nextValue = !allVisible;
        const nextState: Record<string, boolean> = {};
        seriesKeys.forEach((key: string) => {
            nextState[key] = nextValue;
        });

        this.setState({ topBrandsVisibleSeries: nextState });
    };

    private handleTopBrandsSeriesMouseEnter = (seriesKey: string): void => {
        this.setState({ hoveredTopBrandsSeries: seriesKey });
    };

    private handleTopBrandsSeriesMouseLeave = (): void => {
        this.setState({ hoveredTopBrandsSeries: null });
    };

    private formatBrandAxisLabel(value: string): string {
        const label = String(value || "");
        const maxLength = 14;
        if (label.length <= maxLength) {
            return label;
        }
        return `${label.slice(0, maxLength - 1)}…`;
    }

    private getDailyCountsChartData(): { chartData: Array<Record<string, string | number>>; xKey: string; seriesKeys: string[] } {
        const { recsDailyRows } = this.state;
        if (!recsDailyRows.length) {
            return { chartData: [], xKey: "day", seriesKeys: [] };
        }

        const xKey = "day";
        const seriesTotals: Record<string, number> = {};
        const byDay = new Map<string, Record<string, string | number>>();

        recsDailyRows.forEach((row: RecsApiRow) => {
            const day = String(row["day"] ?? "").trim();
            if (!day) {
                return;
            }

            const slotName = String(row["slot_name"] || row["parameters_rule_id"] || "Unknown").trim();
            if (!slotName) {
                return;
            }

            const recPlays = this.toNumber(row["rec_plays"]) ?? 0;

            if (!byDay.has(day)) {
                byDay.set(day, { day });
            }

            const point = byDay.get(day);
            if (!point) {
                return;
            }

            const currentValue = typeof point[slotName] === "number" ? Number(point[slotName]) : 0;
            point[slotName] = currentValue + recPlays;
            seriesTotals[slotName] = (seriesTotals[slotName] || 0) + recPlays;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(byDay.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([, point]) => {
                const normalized: Record<string, string | number> = { ...point };
                seriesKeys.forEach((seriesKey) => {
                    if (typeof normalized[seriesKey] !== "number") {
                        normalized[seriesKey] = 0;
                    }
                });
                return normalized;
            });

        return { chartData, xKey, seriesKeys };
    }

    private getHourlyCountsChartData(): { chartData: Array<Record<string, string | number>>; xKey: string; seriesKeys: string[] } {
        const { recsHourlyRows } = this.state;
        if (!recsHourlyRows.length) {
            return { chartData: [], xKey: "hour_of_day", seriesKeys: [] };
        }

        const xKey = "hour_of_day";
        const seriesTotals: Record<string, number> = {};
        const byHour = new Map<number, Record<string, string | number>>();

        recsHourlyRows.forEach((row: RecsApiRow) => {
            const hourNumber = this.toNumber(row["hour_of_day"]);
            if (hourNumber === null) {
                return;
            }

            const slotName = String(row["slot_name"] || row["parameters_rule_id"] || "Unknown").trim();
            if (!slotName) {
                return;
            }

            const recPlays = this.toNumber(row["rec_plays"]) ?? 0;

            if (!byHour.has(hourNumber)) {
                byHour.set(hourNumber, { "hour_of_day": hourNumber });
            }

            const point = byHour.get(hourNumber);
            if (!point) {
                return;
            }

            const currentValue = typeof point[slotName] === "number" ? Number(point[slotName]) : 0;
            point[slotName] = currentValue + recPlays;
            seriesTotals[slotName] = (seriesTotals[slotName] || 0) + recPlays;
        });

        const seriesKeys = Object.keys(seriesTotals)
            .sort((a, b) => (seriesTotals[b] || 0) - (seriesTotals[a] || 0));

        const chartData = Array.from(byHour.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([, point]) => {
                const normalized: Record<string, string | number> = { ...point };
                seriesKeys.forEach((seriesKey) => {
                    if (typeof normalized[seriesKey] !== "number") {
                        normalized[seriesKey] = 0;
                    }
                });
                return normalized;
            });

        return { chartData, xKey, seriesKeys };
    }

    private formatHourLabel(value: string | number): string {
        const hour = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(hour)) {
            return String(value);
        }

        const normalized = Math.max(0, Math.min(23, Math.floor(hour)));
        const suffix = normalized < 12 ? "AM" : "PM";
        const twoDigitHour = String(normalized).padStart(2, "0");
        return `${twoDigitHour}:00${suffix}`;
    }

    private buildLineMetricConfig(seriesKeys: string[]): MetricConfig[] {
        return seriesKeys.map((key: string, index: number) => ({
            key,
            name: key,
            color: LINE_COLORS[index % LINE_COLORS.length]
        }));
    }

    private formatInteger(value: number | string | null | undefined): string {
        const parsed = this.toNumber(value);
        if (parsed === null) {
            return "-";
        }
        return Math.round(parsed).toLocaleString();
    }

    private formatPlayShare(value: number | string | null | undefined): string {
        const percentage = this.toNumber(value);
        if (percentage === null) {
            return "-";
        }

        return `${percentage.toFixed(1)}%`;
    }

    private isAllRecsSource(parameterRuleId: string): boolean {
        const normalized = parameterRuleId.trim().toLowerCase();
        return normalized === "" || normalized === "unknown" || normalized === "null" || normalized === "undefined";
    }

    private isUnknownSlotName(slotName: string): boolean {
        const normalized = slotName.trim().toLowerCase();
        return normalized === "" || normalized === "unknown" || normalized === "null" || normalized === "undefined";
    }

    private buildRuleHeader(row: RecsRow): string {
        const segments: string[] = [];

        if (!this.isUnknownSlotName(row.slotName)) {
            segments.push(`Slot: ${row.slotName}`);
        }

        if (this.isAllRecsSource(row.parameterRuleId)) {
            segments.push("Source: All recommendations");
        } else {
            segments.push(`Rule: ${row.parameterRuleId}`);
        }

        if (row.modelName) {
            segments.push(`Model: ${row.modelName}`);
        }

        return segments.join(" • ");
    }

    private getHeatmapCell(value: number | string | null | undefined, maxValue: number): ReactNode {
        const parsed = this.toNumber(value);
        const normalized = parsed === null || maxValue <= 0 ? 0 : Math.max(0, Math.min(1, parsed / maxValue));
        const alpha = 0.12 + (0.58 * normalized);

        return (
            <div
                style={{
                    background: `rgba(255, 72, 140, ${alpha})`,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 600,
                    color: "#f8f9fa"
                }}
            >
                {this.formatInteger(value)}
            </div>
        );
    }

    private handleBrandBreakdownSortChange = (key: string): void => {
        this.setState((prevState) => {
            if (prevState.brandBreakdownSort.key === key) {
                return {
                    brandBreakdownSort: {
                        key,
                        direction: prevState.brandBreakdownSort.direction === "asc" ? "desc" : "asc"
                    }
                };
            }

            return {
                brandBreakdownSort: {
                    key,
                    direction: key.includes("rank") ? "asc" : "desc"
                }
            };
        });
    };

    private getBrandBreakdownTableData(): Array<Record<string, string | number | null | undefined>> {
        const rows: Array<Record<string, string | number | null | undefined>> = this.state.recsBrandBreakdownRows.map((row: RecsApiRow, index: number) => ({
            ...row,
            brandDisplay: row["brand_name"] ?? row["brand"] ?? null,
            rowKey: `${String(row["brand_name"] ?? row["brand"] ?? "Unknown")}-${String(row["rec_plays_rank"] ?? "n/a")}-${index}`
        }));

        const { key, direction } = this.state.brandBreakdownSort;
        const multiplier = direction === "asc" ? 1 : -1;

        return rows.sort((a, b) => {
            const aNum = this.toNumber(a[key] as any);
            const bNum = this.toNumber(b[key] as any);

            if (aNum !== null && bNum !== null) {
                return (aNum - bNum) * multiplier;
            }

            const aString = String(a[key] ?? "").toLowerCase();
            const bString = String(b[key] ?? "").toLowerCase();
            return aString.localeCompare(bString) * multiplier;
        });
    }

    private getBrandBreakdownColumnGroups(maxRecPlays: number, maxOverallPlays: number): ColumnGroup[] {
        return [
            {
                name: "Brand",
                columns: [
                    {
                        key: "brandDisplay",
                        header: "Brand",
                        render: (value: string | null | undefined) => value ?? "-",
                        sortable: true
                    }
                ]
            },
            {
                name: "Rec Plays",
                columns: [
                    {
                        key: "rec_plays",
                        header: "Rec Plays",
                        render: (value: number | string | null | undefined) => this.getHeatmapCell(value, maxRecPlays),
                        sortable: true
                    },
                    {
                        key: "rec_plays_rank",
                        header: "Rec Plays Rank",
                        render: (value: number | string | null | undefined) => this.formatInteger(value),
                        sortable: true
                    }
                ]
            },
            {
                name: "Overall",
                columns: [
                    {
                        key: "overall_plays",
                        header: "Overall plays",
                        render: (value: number | string | null | undefined) => this.getHeatmapCell(value, maxOverallPlays),
                        sortable: true
                    },
                    {
                        key: "overall_rank",
                        header: "Overall Rank",
                        render: (value: number | string | null | undefined) => this.formatInteger(value),
                        sortable: true
                    }
                ]
            },
            {
                name: "Share",
                columns: [
                    {
                        key: "play_share",
                        header: "Play Share",
                        render: (value: number | string | null | undefined) => this.formatPlayShare(value),
                        sortable: true
                    }
                ]
            }
        ];
    }

    public render(): ReactNode {
        const {
            filters,
            filterOptions,
            appliedFilters,
            isLoading,
            error,
            recsRows,
            recsDashRows,
            recsDailyRows,
            recsHourlyRows,
            brandBreakdownSort,
            hoveredTopBrandsSeries,
            sidebarOpen,
            sidebarEntries
        } = this.state;
        const { chartData, seriesKeys } = this.getRecsDashChartData();
        const {
            chartData: dailyChartData,
            xKey: dailyXAxisKey,
            seriesKeys: dailySeriesKeys
        } = this.getDailyCountsChartData();
        const {
            chartData: hourlyChartData,
            xKey: hourlyXAxisKey,
            seriesKeys: hourlySeriesKeys
        } = this.getHourlyCountsChartData();
        const chartTheme = getWidgetTheme(true);
        const dailyMetricConfig = this.buildLineMetricConfig(dailySeriesKeys);
        const hourlyMetricConfig = this.buildLineMetricConfig(hourlySeriesKeys);
        const brandBreakdownData = this.getBrandBreakdownTableData();
        const maxRecPlays = Math.max(0, ...brandBreakdownData.map((row) => this.toNumber(row.rec_plays as any) ?? 0));
        const maxOverallPlays = Math.max(0, ...brandBreakdownData.map((row) => this.toNumber(row.overall_plays as any) ?? 0));
        const brandBreakdownColumnGroups = this.getBrandBreakdownColumnGroups(maxRecPlays, maxOverallPlays);
        const visibleTopBrandSeriesKeys = seriesKeys.filter((key: string) => this.isTopBrandsSeriesVisible(key));
        const allTopBrandSeriesVisible = seriesKeys.length > 0 && seriesKeys.every((key: string) => this.isTopBrandsSeriesVisible(key));
        const recsPlaysSidebarEntry = sidebarEntries.find((entry) => entry.widgetKey === "recs_plays");
        const recsPlaysSchema = recsPlaysSidebarEntry?.schema;

        return (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="recs-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.recsRows.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleSidebar}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                    </div>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Recommendations Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />
                    <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "40px" }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: "10px" }}>Loading Recs dashboard...</p>
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <WidgetCard
                                title="Brand Breakdown"
                                description={sidebarEntries.find(e => e.widgetKey === "recs_brand_breakdown")?.description}
                                theme={chartTheme}
                                isEmpty={brandBreakdownData.length === 0}
                                emptyMessage="recs_brand_breakdown returned no rows for current filters."
                                style={{ marginTop: "24px" }}
                            >
                                <MetricsTable
                                    data={brandBreakdownData}
                                    columnGroups={brandBreakdownColumnGroups}
                                    rowKeyField="rowKey"
                                    emptyMessage="No brand breakdown data available"
                                    sortConfig={brandBreakdownSort}
                                    onSortChange={this.handleBrandBreakdownSortChange}
                                />
                            </WidgetCard>

                             <WidgetCard
                                title="Top brands - Play Share"
                                description={sidebarEntries.find(e => e.widgetKey === "recs_attributions")?.description}
                                theme={chartTheme}
                                isEmpty={recsDashRows.length === 0 || chartData.length === 0}
                                emptyMessage="recs_attributions returned no stackable attribution rows for current filters."
                                style={{ marginTop: "24px" }}
                            >
                                {visibleTopBrandSeriesKeys.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        All slots are hidden. Use metric toggles to show at least one slot.
                                    </Alert>
                                ) : (
                                    <div style={{ border: "1px solid #343a40", borderRadius: "8px", background: "#212529", padding: "12px" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                marginBottom: "15px",
                                                flexWrap: "wrap",
                                                gap: "8px"
                                            }}
                                        >
                                            <div>
                                                <span style={{ fontWeight: "bold", color: "#f8f9fa" }}>Visible Metrics</span>
                                                <div style={{ fontSize: "11px", color: "#f8f9fa", opacity: 0.6, marginTop: "2px" }}>
                                                    Click to toggle • Hover to highlight
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                                                {seriesKeys.map((seriesKey: string, index: number) => {
                                                    const isVisible = this.isTopBrandsSeriesVisible(seriesKey);
                                                    const isHovered = hoveredTopBrandsSeries === seriesKey;
                                                    return (
                                                        <div
                                                            key={`top-brands-legend-${seriesKey}`}
                                                            onClick={() => this.handleTopBrandsSeriesToggle(seriesKey)}
                                                            onMouseEnter={() => this.handleTopBrandsSeriesMouseEnter(seriesKey)}
                                                            onMouseLeave={this.handleTopBrandsSeriesMouseLeave}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "4px",
                                                                padding: "4px 8px",
                                                                backgroundColor: isHovered ? "rgba(0,0,0,0.1)" : "#212529",
                                                                borderRadius: "4px",
                                                                border: "1px solid #343a40",
                                                                cursor: "pointer",
                                                                opacity: isVisible ? 1 : 0.4,
                                                                textDecoration: isVisible ? "none" : "line-through"
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    width: "12px",
                                                                    height: "12px",
                                                                    backgroundColor: this.getStackColor(index),
                                                                    borderRadius: "2px",
                                                                    opacity: isVisible ? 1 : 0.4
                                                                }}
                                                            />
                                                            <span
                                                                style={{
                                                                    fontSize: "12px",
                                                                    color: "#f8f9fa",
                                                                    fontWeight: isHovered ? "bold" : "normal"
                                                                }}
                                                            >
                                                                {seriesKey}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    onClick={() => this.handleTopBrandsToggleAll(seriesKeys)}
                                                    style={{
                                                        padding: "4px 10px",
                                                        fontSize: "12px",
                                                        marginLeft: "4px",
                                                        border: "1px solid #6c757d",
                                                        color: "#f8f9fa",
                                                        background: "transparent",
                                                        borderRadius: "4px"
                                                    }}
                                                >
                                                    {allTopBrandSeriesVisible ? "Deselect All" : "Select All"}
                                                </button>
                                            </div>
                                        </div>
                                        <RESP width="100%" height={600}>
                                            <CCHART data={chartData} margin={{ top: 55, right: 30, left: 40, bottom: 10 }}>
                                                <CG strokeDasharray="3 3" stroke="#495057" />
                                                <XA
                                                    dataKey="brandName"
                                                    interval={2}
                                                    height={100}
                                                    tickFormatter={(value: any) => this.formatBrandAxisLabel(String(value))}
                                                    tick={{ 
                                                        angle: -45,
                                                        textAnchor: "end",
                                                        fill: "#f8f9fa", fontSize: 11 
                                                    }}
                                                    
                                                />
                                                <YA
                                                    yAxisId="share"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => `${value.toFixed(1)}%`}
                                                    domain={[0, 100]}
                                                />
                                                <YA
                                                    yAxisId="plays"
                                                    orientation="right"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => value.toLocaleString()}
                                                />
                                                <TT
                                                    formatter={(value: number, name: string) => {
                                                        if (name === "Brand plays") {
                                                            return [typeof value === "number" ? value.toLocaleString() : value, name];
                                                        }

                                                        return [`${Number(value).toFixed(2)}%`, name];
                                                    }}
                                                    contentStyle={{ backgroundColor: "#1b1f23", border: "1px solid #343a40", color: "#f8f9fa" }}
                                                    itemStyle={{ color: "#f8f9fa" }}
                                                    labelStyle={{ color: "#f8f9fa" }}
                                                />
                                                <LG wrapperStyle={{ display: "none" }} />
                                                {visibleTopBrandSeriesKeys.map((seriesKey: string) => {
                                                    const originalIndex = seriesKeys.indexOf(seriesKey);
                                                    const isHovered = hoveredTopBrandsSeries === seriesKey;
                                                    const hasHover = hoveredTopBrandsSeries !== null;
                                                    const fillOpacity = hasHover && !isHovered ? 0.3 : 1;

                                                    return (
                                                    <BR
                                                        key={seriesKey}
                                                        yAxisId="share"
                                                        stackId="slotShare"
                                                        dataKey={seriesKey}
                                                        fill={this.getStackColor(originalIndex)}
                                                        fillOpacity={fillOpacity}
                                                        name={seriesKey}
                                                    />
                                                    );
                                                })}
                                                <LN
                                                    yAxisId="plays"
                                                    type="monotone"
                                                    dataKey="brandPlays"
                                                    stroke="#ff8c42"
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    name="Brand plays"
                                                />
                                            </CCHART>
                                        </RESP>
                                    </div>
                                )}
                            </WidgetCard>

                            {recsRows.length === 0 ? (
                                <Row>
                                    <Col>
                                        <Alert variant="info">No rows with plays &gt; 0 for the selected date range.</Alert>
                                    </Col>
                                </Row>
                            ) : (
                                recsRows.map((row: RecsRow) => (
                                    <RuleSection key={`${row.parameterRuleId}-${row.slotName}-${row.modelName}`}>
                                        <WidgetCard
                                            title={this.buildRuleHeader(row)}
                                            description={recsPlaysSidebarEntry?.description}
                                            theme={chartTheme}
                                        >
                                            {this.renderRecsMetrics(row, recsPlaysSchema)}
                                        </WidgetCard>
                                    </RuleSection>
                                ))
                            )}

                            <WidgetCard
                                title="Recommended Plays"
                                description={sidebarEntries.find(e => e.widgetKey === "recs_daily_counts")?.description}
                                theme={chartTheme}
                                isEmpty={recsDailyRows.length === 0 || dailyChartData.length === 0 || dailySeriesKeys.length === 0}
                                emptyMessage="recs_daily_counts returned no chartable rows for current filters."
                                style={{ marginTop: "24px" }}
                            >
                                <InteractiveLineChart
                                    title=""
                                    data={dailyChartData}
                                    xAxisDataKey={dailyXAxisKey}
                                    metrics={dailyMetricConfig}
                                    theme={chartTheme}
                                    height={380}
                                    tooltipValueFormatter={(value: number) => value.toLocaleString()}
                                />
                            </WidgetCard>

                            <WidgetCard
                                title="Recommended Plays by Hour"
                                description={sidebarEntries.find(e => e.widgetKey === "recs_hourly_counts")?.description}
                                theme={chartTheme}
                                isEmpty={recsHourlyRows.length === 0 || hourlyChartData.length === 0 || hourlySeriesKeys.length === 0}
                                emptyMessage="recs_hourly_counts returned no chartable rows for current filters."
                                style={{ marginTop: "24px" }}
                            >
                                <InteractiveLineChart
                                    title=""
                                    data={hourlyChartData}
                                    xAxisDataKey={hourlyXAxisKey}
                                    metrics={hourlyMetricConfig}
                                    theme={chartTheme}
                                    height={380}
                                    xAxisTickFormatter={(value: string) => this.formatHourLabel(value)}
                                    tooltipLabelFormatter={(value: string) => this.formatHourLabel(value)}
                                    tooltipValueFormatter={(value: number) => value.toLocaleString()}
                                />
                            </WidgetCard>
                        </>
                    )}
                </Container>
                </div>
                <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
                <ScheduleExportModal
                    show={this.state.showScheduleModal}
                    onHide={() => this.setState({ showScheduleModal: false })}
                    filters={this.state.appliedFilters}
                    pageKey="recs_dashboard"
                    pageTitle="Recommendations Dashboard"
                    customerSlug={this.props.customerConfig.slug}
                    editingSchedule={this.state.editingSchedule}
                    onSave={this.handleScheduleSave}
                />
            </div>
        );
    }
}

export default RecsDashboard;
